import {StagedWord} from "../../../../../common/types/words";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box, Card, CardContent, Divider, IconButton, Paper, Popper, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {useAudio} from "../../../../../common/context/AudioContext";
import {getAudioForSource} from "../../../../../common/utils/audioUtils";

export interface ColoredWordCardViewProps {
    word: StagedWord
    backgroundColor?: string
    stageSymbol: 'up' | 'down' | 'none'
    onWordClicked: (word: StagedWord) => void
}

function ColoredWordCardView({word, backgroundColor, stageSymbol, onWordClicked}: ColoredWordCardViewProps) {
    const [popperOpen, setPopperOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);
    const openTimeoutRef = useRef<number | null>(null);
    const {play} = useAudio();
    const theme = useTheme();

    const audio = useMemo(() => getAudioForSource(word.resources?.lemma), [word.resources?.lemma]);

    const exampleSentence = word.exampleSentences?.[0];
    const sentenceAudio = useMemo(
        () => getAudioForSource(word.resources?.sentence),
        [word.resources?.sentence]
    );

    const stageArrow = stageSymbol === 'up'
        ? <ArrowUpwardIcon sx={{color: theme.palette.success.dark, fontWeight: 'bold', mr: 0.5, fontSize: '1rem', verticalAlign: 'middle'}}/>
        : stageSymbol === 'down'
            ? <ArrowDownwardIcon sx={{color: theme.palette.error.dark, fontWeight: 'bold', mr: 0.5, fontSize: '1rem', verticalAlign: 'middle'}}/>
            : null;

    const cancelCloseTimeout = () => {
        if (closeTimeoutRef.current !== null) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const openPopperDelayed = () => {
        cancelCloseTimeout();
        if (openTimeoutRef.current !== null) {
            window.clearTimeout(openTimeoutRef.current);
        }
        openTimeoutRef.current = window.setTimeout(() => {
            setPopperOpen(true);
            openTimeoutRef.current = null;
        }, 400);
    };

    const closePopperDelayed = () => {
        if (openTimeoutRef.current !== null) {
            window.clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
        if (closeTimeoutRef.current !== null) {
            window.clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = window.setTimeout(() => {
            setPopperOpen(false);
            closeTimeoutRef.current = null;
        }, 120);
    };

    useEffect(() => {
        return () => {
            if (openTimeoutRef.current !== null) {
                window.clearTimeout(openTimeoutRef.current);
            }
            if (closeTimeoutRef.current !== null) {
                window.clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Box
            ref={anchorRef}
            onMouseEnter={openPopperDelayed}
            onMouseLeave={closePopperDelayed}
        >
            <Card sx={{backgroundColor, cursor: 'pointer'}}
                  onClick={() => onWordClicked(word)}>
                <CardContent sx={{p: 1, '&:last-child': {pb: 1}}}>
                    <Typography>{word.lemma}</Typography>
                </CardContent>
            </Card>
            <Popper open={popperOpen} anchorEl={anchorRef.current} placement="top" sx={{zIndex: 1300}}>
                <Paper
                    elevation={4}
                    sx={{p: 1.5, maxWidth: 320, cursor: 'pointer'}}
                    onMouseEnter={cancelCloseTimeout}
                    onMouseLeave={closePopperDelayed}
                    onClick={() => onWordClicked(word)}
                >
                    <Typography variant="body2" fontWeight="bold">
                        {word.lemma}
                        {audio && (
                            <IconButton size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            play(audio)
                                        }}
                                        sx={{ml: 0.5, p: 0.25, verticalAlign: 'middle', color: 'black'}}>
                                <VolumeUpIcon fontSize="inherit"/>
                            </IconButton>
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {word.translations.join(', ')}
                    </Typography>
                    {word.partOfSpeech && (
                        <Typography variant="caption" color="text.disabled" sx={{display: 'block', mt: 0.5}}>
                            {word.partOfSpeech}
                        </Typography>
                    )}
                    {exampleSentence && (
                        <>
                            <Divider sx={{my: 1}}/>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <Typography variant="body2" fontStyle="italic">
                                    {exampleSentence.sentence}
                                </Typography>
                                {sentenceAudio && (
                                    <IconButton size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    play(sentenceAudio)
                                                }}
                                                sx={{ml: 0.5, p: 0.25, color: 'black', verticalAlign: 'middle'}}>
                                        <VolumeUpIcon fontSize="inherit"/>
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                {exampleSentence.translation}
                            </Typography>
                        </>
                    )}
                    <Divider sx={{my: 1}}/>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'inline-flex', alignItems: 'center', lineHeight: 1.2}}>
                        {stageArrow}
                        Stage {word.stage}
                    </Typography>
                </Paper>
            </Popper>
        </Box>
    );
}

export default ColoredWordCardView;
